import _jsx from '@babel/runtime/helpers/esm/jsx';
import 'prop-types';
import { createRoot } from 'react-dom/client';
import { match, Router, applyRouterMiddleware } from 'react-router';
import { useScroll } from 'react-router-scroll';
import Sentry from '@dbh/sentry';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import { emptyHashesInSsrStateUpdatedToBrowserHash } from '@dbh/ssr-data-redux';
import '@dbh/redux-types';
import { makeSelectCurrentHashPageReference } from '@dbh/webpages-redux';
import { makeSelectGtagDataToSendWithEveryEvent, analyticsEventSent } from '@dbh/analytics-redux';
import { makeSelectLocale } from '@dbh/routing-redux';
import OverlayErrorFullPage from '@dbh/full-page-error-overlay';
import { limitedUseHardcodedStrings } from '@dbh/limited-use-hardcoded-strings';
import { DEFAULT_LOCALE } from '@dbh/i18n';
import { Map } from 'immutable';
import { jsx } from 'react/jsx-runtime';
import _last from 'lodash/last';
import 'lodash/noop';
import { PAGE_TYPES } from '@dbh/admindev-constants';

/**
 * Returns a `GA4` event to be used with `analyticsEventSent`.
 * @param {Object} options .
 * @param {string} options.value .
 * @param {string} options.outerPageType A `PAGE_TYPE`. @example `HomePage`.
 * @return {Immutable.Map} Matches: `analyticsEventShape`.
 */const trackingTimeToInteractive=withConformsTo("trackingTimeToInteractive",[],a=>{let{value:b,routerPageType:c}=a;return Map({eventPastTense:"timing_complete",eventCategory:"Page Speed",userTimingName:"Time To Interactive",nonInteraction:!0,eventLabel:c,value:b})});var trackingTimeToInteractive$1 = trackingTimeToInteractive;

/**
 * Returns a `GA4` event to be used with `analyticsEventSent`.
 * @return {Immutable.Map} Matches: `analyticsEventShape`.
 */const trackingChunkLoadingFailure=()=>Map({eventPastTense:"ChunkLoadingFailure",eventCategory:"Errors",eventLabel:"Network",nonInteraction:!0});var trackingChunkLoadingFailure$1 = trackingChunkLoadingFailure;

/**
 * Returns a `GA4` event to be used with `analyticsEventSent`.
 * @return {Immutable.Map} Matches: `analyticsEventShape`.
 */const trackingPerformanceNotSupported=()=>Map({eventPastTense:"BrowserApiNotSupported",eventCategory:"DOM",eventLabel:"window.performance",nonInteraction:!0});var trackingPerformanceNotSupported$1 = trackingPerformanceNotSupported;

/**
 * @typedef {import('../types').ReactRouterProps} ReactRouterProps
 */ /**
 * Send custom timing `timing_complete` to `GA4`.
 * @param {ReactRouterProps} renderProps The render props passed by `react-router`.
 * @param {Immutable.Map} store .
 */const sendTimingsToAnalytics=withConformsTo("sendTimingsToAnalytics",[],(a,b)=>{const{routes:c}=a,d=makeSelectGtagDataToSendWithEveryEvent()(b.getState());if(window.performance){// Get the `pageType` from the matched routes.
const a=c.reduce((a,b)=>b.pageType?b.pageType:a,""),b=Math.round(performance.now());// Render just completed, now is "time to interactive".
if(b){const c=trackingTimeToInteractive$1({value:b,routerPageType:a});analyticsEventSent(c.merge(d));}}else {const a=trackingPerformanceNotSupported$1();// `window.performance` is not supported in the current browser.
analyticsEventSent(a.merge(d));}});var sendTimingsToAnalytics$1 = sendTimingsToAnalytics;

/**
 * @typedef {import('../types').ReactRouterProps} ReactRouterProps
 */const shouldUpdateScroll=withConformsTo("shouldUpdateScroll",[],(a,b)=>{const{routes:c,location:d}=b;// The landing page does not have a previous `Router` Prop.
if(!a)// Use has just arrived on our site,
// if the link has an anchor we let the browser move it in view.
return !d.hash;const{routes:e,location:f}=a,{pageType:g}=_last(e),{pageType:h}=_last(c),i=h===PAGE_TYPES.AllCategoryCityPage&&g===PAGE_TYPES.AllCategoryCityPage;// We don't want to scroll to top when the page number is changed with the
// paginator in `AllCategoryCityPage`, we already do that with the paginator
// component.
if(i)return !1;// We do not want scroll to top on location change from `HdpPage` to `HdpPage`
// because we already imperatively scroll to our offers, when we change date
// using `DateChanger` widget.
const j=g===PAGE_TYPES.HdpPage&&h===PAGE_TYPES.HdpPage,k=d.pathname===f.pathname&&d.search===f.search&&d.state===f.state;// Required for the followign reasons:
// 1. browsers move the scroll position to put the anchor in the viewport,
//    we want to preserve this behaviour.
// 2. we need this change to prevent the scroll to top when we transition to
//    a `TopMenuHashPage`.
return !(j||k)});/**
 * Customization function passed to `react-router-scroll/useScroll`.
 * It allows to define custom scroll behaviour on location change.
 * @param {ReactRouterProps?} prevRouterProps Previous router props, `null`
 * if landing page.
 * @param {ReactRouterProps} currentRouterProps Current Router props.
 * @return {boolean} Should the location change trigger a scroll restoration. If
 * an `Array [x, y]` was returned, `react-router-scroll` would set
 * `window.scrollX = x` and `window.scrollY = y`.
 * @see {@link https://github.com/taion/react-router-scroll#custom-scroll-behavior}
 */var shouldUpdateScroll$1 = shouldUpdateScroll;

/**
 * Merge the provided query parameters in the current browser `location`, and
 * return a string containing the full `href`. Browser only function.
 * @param {Object} newQueryParams The new query parameters.
 * @return {string} The current location `href`, with the provided query added
 * to it.
 */const getLocationHrefWithQuery=withConformsTo("getLocationHrefWithQuery",[],a=>{const b=new URLSearchParams(location.search);for(const[c,d]of Object.entries(a))b.set(c,d);const c=b.toString();return location.href.replace(location.search,"")+"?"+c});var getLocationHrefWithQuery$1 = getLocationHrefWithQuery;

/**
 * Reload the current page but append to it a query parameter that logs the
 * fact that a network failure has happened (`dbh_reloaded_after_network_failure`)
 * and another, random parameter (`dbh_cache_bust`) that encourages the browser
 * to refresh the cache (in case the current browser has an old build of our
 * app cached).
 */const reloadLocationAfterNetworkFailure=()=>{document.location.assign(getLocationHrefWithQuery$1({dbh_reloaded_after_network_failure:!0,dbh_cache_bust:Math.random().toString(36).replace(/[^a-z]+/g,"")}));};var reloadLocationAfterNetworkFailure$1 = reloadLocationAfterNetworkFailure;

const getLimitedUseHardcodedString=withConformsTo("getLimitedUseHardcodedString",[],(a,b)=>{const c=limitedUseHardcodedStrings["hydra:member"];let d=c.find(c=>c.culture===(a||DEFAULT_LOCALE)&&c.code===b);return d||(d=c.find(a=>a.culture===DEFAULT_LOCALE&&a.code===b)),d?d.message:void 0});var getLimitedUseHardcodedString$1 = getLimitedUseHardcodedString;

/**
 * Renders a "bad connection/site updated, try again" message. To be used when
 * there has been a network (chunk) loading failure due to unstable connection
 * or the chunk simply is not there anymore on our server (new release on
 * our server, but the user has an old release cached in the browser, that
 * requests "old chunks").
 * @param {Object} options .
 * @param {string?} options.locale The current locale. @example `en-GB`.
 */const renderNetworkFailureUI=withConformsTo("renderNetworkFailureUI",[],a=>{const{locale:b}=a,c=document.getElementById("app"),d=document.createElement("div");c.appendChild&&c.appendChild(d);const e=getLimitedUseHardcodedString$1(b,"manual.hardcoded.network_loading_failure"),f=createRoot(d);f.render(/*#__PURE__*/_jsx(OverlayErrorFullPage,{shouldLoadChunkAndMount:!0,onClick:reloadLocationAfterNetworkFailure$1},void 0,e));});var renderNetworkFailureUI$1 = renderNetworkFailureUI;

/**
 * We could not render our app in the browser because `renderProps` was falsy,
 * meaning that there was a network issue; some `webpack` chunks could not be
 * loaded. We log the exception and try to show a "network failure UI" with a
 * very visible link that lets the user reload the page and "try again".
 * @param {Object} store .
 */const handleRenderInBrowserNotPossible=withConformsTo("handleRenderInBrowserNotPossible",[],a=>{const b=a?makeSelectLocale()(a.getState()):void 0,c=b||(document.location.pathname.includes("it-IT")?"it-IT":"en-GB");renderNetworkFailureUI$1({locale:c});const d=a.getState(),e=makeSelectGtagDataToSendWithEveryEvent()(d),f=trackingChunkLoadingFailure$1();analyticsEventSent(f.merge(e)),Sentry.captureException("(`handleRenderInBrowserNotPossible`) Expected `renderProps` to be defined. Probably one or more `webpack` chunks have failed to load, becauseof bad connection or because a new release went live. When a new release goes live, the chunk(s) change, and browsers that have in their cache an older version of the front end app, may request \"old\" chunks that do not exist anymore on our server, since they are part of a previous build.");});var handleRenderInBrowserNotPossible$1 = handleRenderInBrowserNotPossible;

/**
 * @typedef {import('./types').ReactRouterProps} ReactRouterProps
 */ /**
 * Called when `ReactDOM` has finished rendering in the browser.
 * @param {Object} store The `redux` store.
 * @param {ReactRouterProps?} renderProps The render props passed by `react-router`.
 * @see {@link https://reactjs.org/docs/react-dom.html#render}
 */const handleRenderInBrowserComplete=withConformsTo("handleRenderInBrowserComplete",[],(a,b)=>{if(b){sendTimingsToAnalytics$1(b,a);const{location:{hash:c}}=window;c&&makeSelectCurrentHashPageReference()(a.getState())&&a.dispatch(emptyHashesInSsrStateUpdatedToBrowserHash({hash:c}));}else handleRenderInBrowserNotPossible$1(a);});var handleRenderInBrowserComplete$1 = handleRenderInBrowserComplete;

const renderInBrowser=withConformsTo("renderInBrowser",[],a=>{let{store:b,routes:c,redirectTo:d,history:e,AppRoot:f}=a;match({history:e,routes:c},(a,c,g)=>{// Write `renderProps = undefined;` here to test the "network failure page".
if(d&&e.push(d),g)// Catch the rendering errors here otherwise `react-router` `match` will
// swallow them.
try{const a=createRoot(document.getElementById("app")),c=handleRenderInBrowserComplete$1(b,g);a.render(/*#__PURE__*/ // @see {@link https://github.com/reactwg/react-18/discussions/5#discussion-3385584}
_jsx(f,{renderCallback:c,store:b},void 0,/*#__PURE__*/jsx(Router,{...g,render:// Scroll to top when going to a new page, imitating default browser
// behaviour.
applyRouterMiddleware(useScroll(shouldUpdateScroll$1))})));}catch(a){Sentry.captureException(a),console.error(a);}else// Network failure: either the user's browser is using a cached old
// build, that requested chunks that do not exist anymore on our front
// end server, or the user has the latest build, but the connection
// being used is poor, perhaps `3G`.
handleRenderInBrowserNotPossible$1(b);});});

export { renderInBrowser as default };
